import { Injectable } from '@angular/core';
import * as XLSX from 'xlsx';
import * as ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { NGP_LOGO_BASE64 } from '../constants/ngp-logo.constant';
import {
  Document,
  Packer,
  Paragraph,
  TextRun,
  Table,
  TableRow,
  TableCell,
  WidthType,
  AlignmentType,
  VerticalAlign,
  BorderStyle,
  Header,
  Footer,
  PageNumber,
  ImageRun,
  PageOrientation,
  TableLayoutType
} from 'docx';

@Injectable({
  providedIn: 'root',
})
export class ExportService {
  constructor() {}

  /**
   * Export data to Excel
   * @param data - The array of objects to export
   * @param fileName - The name of the Excel file (optional, default: 'ExportedData.xlsx')
   * @param sheetName - The name of the worksheet (optional, default: 'Sheet1')
   */
  exportToExcel(data: any[], fileName: string = 'ExportedData.xlsx', sheetName: string = 'Sheet1', colWidths?: any[]): void {
    if (!data || !data.length) {
      console.warn('No data available to export.');
      return;
    }

    // Convert JSON data to worksheet
    const worksheet = XLSX.utils.json_to_sheet(data);

    if (colWidths && colWidths.length) {
      worksheet['!cols'] = colWidths;
    }

    // Create a new workbook and append the worksheet
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);

    // Generate the Excel file buffer
    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });

    // Create a Blob from the buffer and save as file
    const blob = new Blob([excelBuffer], { type: 'application/octet-stream' });
    saveAs(blob, fileName.endsWith('.xlsx') ? fileName : `${fileName}.xlsx`);
  }

  /**
   * Export data to CSV
   * @param data - The array of objects to export
   * @param fileName - The name of the CSV file
   */
  exportToCSV(data: any[], fileName: string = 'ExportedData.csv'): void {
    if (!data || !data.length) {
      console.warn('No data available to export.');
      return;
    }

    const worksheet = XLSX.utils.json_to_sheet(data);
    const csvOutput = XLSX.utils.sheet_to_csv(worksheet);
    const blob = new Blob([csvOutput], { type: 'text/csv;charset=utf-8;' });
    saveAs(blob, fileName.endsWith('.csv') ? fileName : `${fileName}.csv`);
  }

  /**
   * Export data directly to PDF file download
   * @param data - The array of objects to export
   * @param fileName - The name of the PDF file
   * @param title - The title of the document
   */
  exportToPDF(data: any[], fileName: string = 'ExportedData.pdf', title: string = 'Exported Report'): void {
    if (!data || !data.length) {
      console.warn('No data available to export.');
      return;
    }

    try {
      const doc = new jsPDF({
        orientation: 'landscape',
        unit: 'pt',
        format: 'a4'
      });

      const headers = Object.keys(data[0]);
      const rows = data.map(item =>
        headers.map(header => item[header] !== undefined && item[header] !== null ? String(item[header]) : '')
      );

      // Title & Metadata
      doc.setFontSize(14);
      doc.setTextColor(30, 41, 59);
      doc.text(title, 40, 35);

      doc.setFontSize(9);
      doc.setTextColor(100, 116, 139);
      doc.text(`Generated on ${new Date().toLocaleString()}`, 40, 50);

      autoTable(doc, {
        head: [headers],
        body: rows,
        startY: 60,
        styles: {
          fontSize: 7,
          cellPadding: 3,
          overflow: 'linebreak'
        },
        headStyles: {
          fillColor: [37, 99, 235],
          textColor: [255, 255, 255],
          fontStyle: 'bold'
        },
        alternateRowStyles: {
          fillColor: [248, 250, 252]
        },
        theme: 'grid'
      });

      const pdfFileName = fileName.endsWith('.pdf') ? fileName : `${fileName}.pdf`;
      doc.save(pdfFileName);
    } catch (e) {
      console.error('jsPDF export error, falling back to print window:', e);
      const headers = Object.keys(data[0]);
      const printWindow = window.open('', '_blank');
      if (!printWindow) {
        alert('Please allow popups to export PDF.');
        return;
      }

      const rowsHtml = data.map(row => `
        <tr>
          ${headers.map(h => `<td style="border: 1px solid #e2e8f0; padding: 7px 6px; font-size: 10px;">${row[h] !== undefined && row[h] !== null ? row[h] : ''}</td>`).join('')}
        </tr>
      `).join('');

      const headersHtml = headers.map(h => `<th style="border: 1px solid #cbd5e1; background-color: #2563eb; color: #ffffff; padding: 8px 6px; font-size: 10px; text-align: left; white-space: nowrap;">${h}</th>`).join('');

      const htmlContent = `
        <!DOCTYPE html>
        <html>
        <head>
          <title>${title}</title>
          <style>
            body { font-family: 'Segoe UI', Roboto, Helvetica, Arial, sans-serif; margin: 15px; color: #1e293b; }
            h2 { color: #1e293b; font-size: 18px; margin-bottom: 4px; }
            p.subtitle { color: #64748b; font-size: 11px; margin-top: 0; margin-bottom: 16px; }
            table { width: 100%; border-collapse: collapse; margin-top: 8px; }
            tr:nth-child(even) { background-color: #f8fafc; }
            @media print {
              body { margin: 0; }
              @page { size: landscape; margin: 8mm; }
            }
          </style>
        </head>
        <body>
          <h2>${title}</h2>
          <p class="subtitle">Generated on ${new Date().toLocaleString()}</p>
          <table>
            <thead>
              <tr>${headersHtml}</tr>
            </thead>
            <tbody>
              ${rowsHtml}
            </tbody>
          </table>
          <script>
            window.onload = function() {
              window.print();
            };
          </script>
        </body>
        </html>
      `;

      printWindow.document.write(htmlContent);
      printWindow.document.close();
    }
  }

  /**
   * Export Supply Sense data as custom NGP Quotation Form (QF sheet) - Pure 15 Columns Table
   */
  async exportToNgpQuotationForm(
    rows: any[],
    fileName: string = 'NGP_Quotation_Form.xlsx',
    options: {
      reviewerName?: string;
      dateStr?: string;
      projectName?: string;
      customerName?: string;
      customerGst?: string;
      salutationName?: string;
      buyerName?: string;
      buyerAddress?: string;
      buyerEmail?: string;
      buyerPhone?: string;
      quotationNo?: string;
      validUntilStr?: string;
      sellerCompanyName?: string;
      sellerAddress?: string;
      sellerGst?: string;
      sellerPhone?: string;
      sellerEmail?: string;
      salesAgent?: string;
      poPlacementAddress?: string;
      quotationNotes?: string;
      footerCompanyName?: string;
      footerAddress?: string;
    } = {}
  ): Promise<void> {
    if (!rows || !rows.length) {
      console.warn('No data available to export.');
      return;
    }

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Quotation Data', {
      views: [{ showGridLines: true }]
    });

    // 1. Column Widths (A to O: 15 columns matching Quotation Table)
    worksheet.columns = [
      { width: 6 },  // A (1): SL
      { width: 18 }, // B (2): Customer Ref.
      { width: 22 }, // C (3): Manufacturer Part No.
      { width: 18 }, // D (4): Alternate Quoted
      { width: 34 }, // E (5): Description
      { width: 16 }, // F (6): Make
      { width: 12 }, // G (7): HSN
      { width: 8 },  // H (8): UOM
      { width: 10 }, // I (9): Qty
      { width: 12 }, // J (10): MOQ/SPQ
      { width: 14 }, // K (11): Unit Price
      { width: 16 }, // L (12): Total Price
      { width: 12 }, // M (13): Lead Time
      { width: 10 }, // N (14): COO
      { width: 18 }, // O (15): Remarks
    ];

    const lightBorder: Partial<ExcelJS.Borders> = {
      top: { style: 'thin', color: { argb: 'FFCBD5E1' } },
      left: { style: 'thin', color: { argb: 'FFCBD5E1' } },
      bottom: { style: 'thin', color: { argb: 'FFCBD5E1' } },
      right: { style: 'thin', color: { argb: 'FFCBD5E1' } }
    };

    // 2. Table Headers (Row 1 - Pure 15 Columns Table)
    const tableHeaders = [
      'SL', 'Customer Ref.', 'Manufacturer Part No.', 'Alternate Quoted', 'Description',
      'Make', 'HSN', 'UOM', 'Qty', 'MOQ/SPQ',
      'Unit Price', 'Total Price', 'Lead Time', 'COO', 'Remarks'
    ];

    const headerRow = worksheet.getRow(1);
    headerRow.height = 26;
    tableHeaders.forEach((h, idx) => {
      const colIdx = idx + 1;
      const cell = headerRow.getCell(colIdx);
      cell.value = h;
      cell.font = { name: 'Calibri', size: 10, bold: true, color: { argb: 'FFFFFFFF' } };
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF002060' } };
      cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
      cell.border = {
        top: { style: 'thin', color: { argb: 'FF001040' } },
        left: { style: 'thin', color: { argb: 'FF001040' } },
        bottom: { style: 'thin', color: { argb: 'FF001040' } },
        right: { style: 'thin', color: { argb: 'FF001040' } }
      };
    });

    // 3. Populate Data Rows (Row 2 onwards)
    let subtotal = 0;
    rows.forEach((row, i) => {
      const r = 2 + i;
      const dataRow = worksheet.getRow(r);
      dataRow.height = 22;

      const qty = Number(row.quantity ?? row.projectQty ?? 0);
      const unitPrice = Number(row.unitPrice ?? 0);
      const rowTotal = (row.totalPrice !== undefined && row.totalPrice !== null && !isNaN(Number(row.totalPrice)))
        ? Number(row.totalPrice)
        : (qty * unitPrice);
      subtotal += rowTotal;

      // Col A (1): SL
      const cA = dataRow.getCell(1);
      cA.value = row.slNo ?? (i + 1);
      cA.alignment = { vertical: 'middle', horizontal: 'center' };

      // Col B (2): Customer Ref.
      const cB = dataRow.getCell(2);
      cB.value = row.partNumber ?? '';
      cB.alignment = { vertical: 'middle', horizontal: 'left' };

      // Col C (3): Manufacturer Part No.
      const cC = dataRow.getCell(3);
      cC.value = row.quotedPart ?? '';
      cC.alignment = { vertical: 'middle', horizontal: 'left' };

      // Col D (4): Alternate Quoted
      const cD = dataRow.getCell(4);
      cD.value = row.alternateQuoted ?? '';
      cD.alignment = { vertical: 'middle', horizontal: 'left' };

      // Col E (5): Description
      const cE = dataRow.getCell(5);
      cE.value = row.description ?? '';
      cE.alignment = { vertical: 'middle', horizontal: 'left', wrapText: true };

      // Col F (6): Make
      const cF = dataRow.getCell(6);
      cF.value = row.make ?? '';
      cF.alignment = { vertical: 'middle', horizontal: 'left' };

      // Col G (7): HSN
      const cG = dataRow.getCell(7);
      cG.value = row.hsnCode ?? '';
      cG.alignment = { vertical: 'middle', horizontal: 'center' };

      // Col H (8): UOM
      const cH = dataRow.getCell(8);
      cH.value = row.uom ?? 'EA';
      cH.alignment = { vertical: 'middle', horizontal: 'center' };

      // Col I (9): Qty
      const cI = dataRow.getCell(9);
      cI.value = qty;
      cI.numFmt = '#,##0';
      cI.alignment = { vertical: 'middle', horizontal: 'right' };

      // Col J (10): MOQ/SPQ
      const cJ = dataRow.getCell(10);
      cJ.value = row.moqSpq !== undefined && row.moqSpq !== null ? String(row.moqSpq) : '';
      cJ.alignment = { vertical: 'middle', horizontal: 'center' };

      // Col K (11): Unit Price
      const cK = dataRow.getCell(11);
      cK.value = unitPrice;
      cK.numFmt = '#,##0.00';
      cK.alignment = { vertical: 'middle', horizontal: 'right' };

      // Col L (12): Total Price
      const cL = dataRow.getCell(12);
      cL.value = {
        formula: `I${r}*K${r}`,
        result: rowTotal
      };
      cL.numFmt = '#,##0.00';
      cL.alignment = { vertical: 'middle', horizontal: 'right' };

      // Col M (13): Lead Time
      const cM = dataRow.getCell(13);
      cM.value = row.leadTime !== undefined && row.leadTime !== null ? String(row.leadTime) : '';
      cM.alignment = { vertical: 'middle', horizontal: 'center' };

      // Col N (14): COO
      const cN = dataRow.getCell(14);
      cN.value = row.coo ?? '';
      cN.alignment = { vertical: 'middle', horizontal: 'center' };

      // Col O (15): Remarks
      const cO = dataRow.getCell(15);
      cO.value = row.remarks ?? '';
      cO.alignment = { vertical: 'middle', horizontal: 'left' };

      // Styling & Alternating Row Fill
      const isEvenRow = (i % 2 === 0);
      const rowFill: ExcelJS.Fill = isEvenRow
        ? { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFFFFF' } }
        : { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF8FAFC' } };

      for (let c = 1; c <= 15; c++) {
        const cell = dataRow.getCell(c);
        cell.border = lightBorder;
        cell.font = { name: 'Calibri', size: 9.5 };
        cell.fill = rowFill;
      }
    });

    const dataEndRow = 1 + rows.length;

    // 4. Total Row directly below the table
    const totalRow = worksheet.getRow(dataEndRow + 1);
    totalRow.height = 22;

    for (let c = 1; c <= 15; c++) {
      const cell = totalRow.getCell(c);
      cell.border = {
        top: { style: 'thin', color: { argb: 'FF001040' } },
        bottom: { style: 'double', color: { argb: 'FF001040' } }
      };
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF8FAFC' } };
    }

    const lblCell = totalRow.getCell(11); // Col K
    lblCell.value = 'Total:';
    lblCell.font = { name: 'Calibri', size: 10, bold: true, color: { argb: 'FF002060' } };
    lblCell.alignment = { vertical: 'middle', horizontal: 'right' };

    const sumCell = totalRow.getCell(12); // Col L
    sumCell.value = {
      formula: `SUM(L2:L${dataEndRow})`,
      result: subtotal
    };
    sumCell.font = { name: 'Calibri', size: 10, bold: true, color: { argb: 'FF002060' } };
    sumCell.alignment = { vertical: 'middle', horizontal: 'right' };
    sumCell.numFmt = '#,##0.00';

    // 5. Configure print settings: A4 landscape, fit to 1 page wide
    worksheet.pageSetup = {
      orientation: 'landscape',
      paperSize: 9, // A4
      fitToPage: true,
      fitToWidth: 1,
      fitToHeight: 0,
      margins: {
        left: 0.3,
        right: 0.3,
        top: 0.4,
        bottom: 0.4,
        header: 0.2,
        footer: 0.2
      }
    };

    // 6. Write buffer and trigger browser download
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    });
    saveAs(blob, fileName.endsWith('.xlsx') ? fileName : `${fileName}.xlsx`);
  }

  /**
   * Export Supply Sense data as custom NGP Quotation Form in PDF format with Terms & Conditions
   */
  exportToNgpQuotationPdf(
    rows: any[],
    fileName: string = 'NGP_Quotation.pdf',
    options: {
      reviewerName?: string;
      dateStr?: string;
      projectName?: string;
      quotationNo?: string;
      validUntilStr?: string;
      sellerCompanyName?: string;
      sellerAddress?: string;
      sellerGst?: string;
      sellerPhone?: string;
      sellerEmail?: string;
      salesAgent?: string;
      customerName?: string;
      customerGst?: string;
      salutationName?: string;
      buyerName?: string;
      buyerAddress?: string;
      buyerEmail?: string;
      buyerPhone?: string;
      poPlacementAddress?: string;
      quotationNotes?: string;
      footerCompanyName?: string;
      footerAddress?: string;
    } = {}
  ): void {
    if (!rows || !rows.length) {
      console.warn('No data available to export.');
      return;
    }

    const reviewerName = (options.reviewerName || 'KAVYA').trim().toUpperCase();
    const salesAgent = (options.salesAgent || reviewerName).trim();
    const dateStr = options.dateStr || (() => {
      const now = new Date();
      const dd = String(now.getDate()).padStart(2, '0');
      const mm = String(now.getMonth() + 1).padStart(2, '0');
      const yyyy = now.getFullYear();
      return `${dd}-${mm}-${yyyy}`;
    })();
    const projectName = options.projectName || '';

    const sellerCompanyName = (options.sellerCompanyName || 'NGP SOLUTIONS').trim();
    const sellerAddress = (options.sellerAddress || 'JR URBANIA PRIME, # 969 & 970, LINGAPURA VILLAGE, ANEKAL TALUK, BANGALORE – 562106').trim();
    const sellerGst = (options.sellerGst || '29BCWPD5012J1ZC').trim();
    const sellerEmail = (options.sellerEmail || 'sales@vigroup.in').trim();
    const sellerPhone = (options.sellerPhone || '702617646').trim();

    const customerName = (options.customerName || projectName || '').trim();
    const customerGst = (options.customerGst || '').trim();
    const salutationName = (options.salutationName || options.buyerName || '').trim();
    const buyerName = (options.buyerName || '').trim();
    const buyerAddress = (options.buyerAddress || '').trim();
    const buyerEmail = (options.buyerEmail || '').trim();
    const buyerPhone = (options.buyerPhone || '').trim();

    const doc = new jsPDF({
      orientation: 'landscape',
      unit: 'pt',
      format: 'a4'
    });

    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 28;

    // 1. Header: Logo
    try {
      doc.addImage(NGP_LOGO_BASE64, 'JPEG', margin, 18, 65, 38);
    } catch (e) {
      console.warn('Could not add logo to PDF:', e);
    }

    // 2. Header: Title & Subtitle (Center)
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(14);
    doc.setTextColor(0, 32, 96); // #002060
    doc.text(sellerCompanyName, pageWidth / 2, 28, { align: 'center' });

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.setTextColor(51, 65, 85);
    doc.text('ENQUIRY REVIEW AND QUOTATION FORM', pageWidth / 2, 42, { align: 'center' });

    if (projectName) {
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      doc.setTextColor(100, 116, 139);
      doc.text(projectName, pageWidth / 2, 53, { align: 'center' });
    }

    // 3. Header: Metadata Box (Right)
    const metaBoxX = pageWidth - margin - 150;
    const metaBoxY = 16;
    const metaBoxW = 150;
    const metaBoxH = 46;

    doc.setDrawColor(203, 213, 225);
    doc.setFillColor(248, 250, 252);
    doc.roundedRect(metaBoxX, metaBoxY, metaBoxW, metaBoxH, 3, 3, 'FD');

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7.5);
    doc.setTextColor(71, 85, 105);
    doc.text('Doc. Ref.:', metaBoxX + 6, metaBoxY + 12);
    doc.setFont('helvetica', 'normal');
    doc.text('NGP-MKT-QF-02', metaBoxX + 55, metaBoxY + 12);

    doc.setFont('helvetica', 'bold');
    doc.text('Revision:', metaBoxX + 6, metaBoxY + 22);
    doc.setFont('helvetica', 'normal');
    doc.text('00', metaBoxX + 55, metaBoxY + 22);

    doc.setFont('helvetica', 'bold');
    doc.text('Date:', metaBoxX + 6, metaBoxY + 32);
    doc.setFont('helvetica', 'normal');
    doc.text(dateStr, metaBoxX + 55, metaBoxY + 32);

    doc.setFont('helvetica', 'bold');
    doc.text('Reviewed By:', metaBoxX + 6, metaBoxY + 42);
    doc.setFont('helvetica', 'normal');
    doc.text(salesAgent || reviewerName, metaBoxX + 62, metaBoxY + 42);

    // 3.5. Address Details: Customer Details on Left, Sales Agent on Right (2 Columns)
    const infoBoxY = 62;
    const infoBoxH = 50;
    const totalContentW = pageWidth - (margin * 2);
    const boxW = (totalContentW - 12) / 2;
    const leftBoxX = margin;
    const rightBoxX = margin + boxW + 12;

    // --- Left Box: Customer Details ---
    doc.setDrawColor(203, 213, 225);
    doc.setFillColor(248, 250, 252);
    doc.roundedRect(leftBoxX, infoBoxY, boxW, infoBoxH, 3, 3, 'FD');

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8);
    doc.setTextColor(0, 32, 96);
    doc.text(customerName || 'CUSTOMER', leftBoxX + 8, infoBoxY + 11);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(6.8);
    doc.setTextColor(51, 65, 85);
    const custAddrLines = doc.splitTextToSize(buyerAddress || 'Address: Available on request', boxW - 16);
    doc.text(custAddrLines.slice(0, 2), leftBoxX + 8, infoBoxY + 21);

    const custBottomY = infoBoxY + 40;
    if (customerGst) {
      doc.setFont('helvetica', 'bold');
      doc.text('GST:', leftBoxX + 8, custBottomY);
      doc.setFont('helvetica', 'normal');
      const cleanGst = customerGst.replace(/^GST:\s*/i, '');
      doc.text(cleanGst, leftBoxX + 28, custBottomY);
    } else {
      doc.setFont('helvetica', 'italic');
      doc.text('Customer Reference: ' + (customerName || projectName), leftBoxX + 8, custBottomY);
    }

    // --- Right Box: Sales Agent & Contact Details ---
    doc.setDrawColor(203, 213, 225);
    doc.setFillColor(248, 250, 252);
    doc.roundedRect(rightBoxX, infoBoxY, boxW, infoBoxH, 3, 3, 'FD');

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7.5);
    doc.setTextColor(0, 32, 96);
    doc.text('YOUR SALES AGENT : ' + (salesAgent || reviewerName), rightBoxX + 8, infoBoxY + 11);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(6.8);
    doc.setTextColor(51, 65, 85);

    let rightLineY = infoBoxY + 21;
    doc.setFont('helvetica', 'bold');
    doc.text('Email:', rightBoxX + 8, rightLineY);
    doc.setFont('helvetica', 'normal');
    doc.text(sellerEmail, rightBoxX + 32, rightLineY);

    if (sellerPhone) {
      doc.setFont('helvetica', 'bold');
      doc.text('Ph:', rightBoxX + 170, rightLineY);
      doc.setFont('helvetica', 'normal');
      doc.text(sellerPhone, rightBoxX + 186, rightLineY);
    }

    const rightBottomY = infoBoxY + 40;
    if (buyerName) {
      doc.setFont('helvetica', 'bold');
      doc.text('Buyer:', rightBoxX + 8, rightBottomY);
      doc.setFont('helvetica', 'normal');
      doc.text(buyerName, rightBoxX + 32, rightBottomY);
    }
    if (buyerEmail) {
      doc.setFont('helvetica', 'bold');
      doc.text('Email ID:', rightBoxX + 140, rightBottomY);
      doc.setFont('helvetica', 'normal');
      doc.text(buyerEmail, rightBoxX + 172, rightBottomY);
    }

    // 3.8. Salutation Line (Dear Mr. ...)
    const salutationTarget = salutationName ? salutationName : '_______________';
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7);
    doc.setTextColor(30, 41, 59);
    const introText = `Dear Mr. ${salutationTarget} Thank you for your valuable RFQ and the opportunity to participate in your sourcing requirements. Based on your demands and specifications, we have carefully reviewed the requirements and worked out our most competitive pricing for your kind review and consideration.`;
    const introLines = doc.splitTextToSize(introText, totalContentW);
    doc.text(introLines, margin, 120);

    const tableStartY = 120 + (introLines.length * 8.5) + 4;

    // Calculate financial totals
    let subtotal = 0;
    rows.forEach(r => {
      const qty = Number(r.quantity ?? r.projectQty ?? 0);
      const unitPrice = Number(r.unitPrice ?? 0);
      const rowTotal = (r.totalPrice !== undefined && r.totalPrice !== null && !isNaN(Number(r.totalPrice)))
        ? Number(r.totalPrice)
        : (qty * unitPrice);
      subtotal += rowTotal;
    });

    const cgst = subtotal * 0.09;
    const sgst = subtotal * 0.09;
    const grandTotal = subtotal + cgst + sgst;

    // 4. AutoTable Data (15 Columns matching Quotation Template)
    const tableHeaders = [
      'SL', 'Customer Ref.', 'Manufacturer Part No.', 'Alternate Quoted', 'Description',
      'Make', 'HSN', 'UOM', 'Qty', 'MOQ/SPQ',
      'Unit Price', 'Total Price', 'Lead Time', 'COO', 'Remarks'
    ];

    const formatCurrency = (val: any) => {
      const num = Number(val);
      return isNaN(num) || num <= 0 ? '-' : num.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    };

    const tableData = rows.map((r, idx) => {
      const qty = Number(r.quantity ?? r.projectQty ?? 0);
      const unitPrice = Number(r.unitPrice ?? 0);
      const total = (r.totalPrice !== undefined && r.totalPrice !== null && !isNaN(Number(r.totalPrice)))
        ? Number(r.totalPrice)
        : (qty * unitPrice);

      return [
        r.slNo ?? (idx + 1),
        r.partNumber || '-',
        r.quotedPart || '-',
        r.alternateQuoted || '-',
        r.description || '-',
        r.make || '-',
        r.hsnCode || '-',
        r.uom || 'EA',
        qty ? qty.toLocaleString('en-IN') : '0',
        r.moqSpq || '-',
        formatCurrency(unitPrice),
        formatCurrency(total),
        r.leadTime || '-',
        r.coo || '-',
        r.remarks || '-'
      ];
    });

    autoTable(doc, {
      head: [tableHeaders],
      body: tableData,
      startY: tableStartY,
      margin: { left: margin, right: margin, bottom: 25 },
      styles: {
        font: 'helvetica',
        fontSize: 6.8,
        cellPadding: 2.5,
        overflow: 'linebreak',
        textColor: [0, 0, 0],
        lineColor: [203, 213, 225],
        lineWidth: 0.5
      },
      headStyles: {
        fillColor: [0, 32, 96],
        textColor: [255, 255, 255],
        fontStyle: 'bold',
        fontSize: 7,
        halign: 'center'
      },
      alternateRowStyles: {
        fillColor: [248, 250, 252]
      },
      columnStyles: {
        0: { halign: 'center', cellWidth: 18 },
        1: { halign: 'left', cellWidth: 55 },
        2: { halign: 'left', cellWidth: 68 },
        3: { halign: 'left', cellWidth: 52 },
        4: { halign: 'left' },
        5: { halign: 'left', cellWidth: 55 },
        6: { halign: 'center', cellWidth: 34 },
        7: { halign: 'center', cellWidth: 22 },
        8: { halign: 'right', cellWidth: 32 },
        9: { halign: 'center', cellWidth: 38 },
        10: { halign: 'right', cellWidth: 50 },
        11: { halign: 'right', cellWidth: 54 },
        12: { halign: 'center', cellWidth: 36 },
        13: { halign: 'center', cellWidth: 24 },
        14: { halign: 'left', cellWidth: 46 }
      },
      theme: 'grid',
      didDrawPage: () => {
        const pageNum = doc.getNumberOfPages();
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(7.5);
        doc.setTextColor(148, 163, 184);
        const footerCompany = options.footerCompanyName || sellerCompanyName;
        const footerAddr = options.footerAddress || sellerAddress;
        const footerText = footerAddr ? `${footerCompany} - ${footerAddr}` : `${footerCompany} - Confidential Customer Quotation`;
        doc.text(footerText.length > 110 ? footerText.substring(0, 107) + '...' : footerText, margin, pageHeight - 12);
        doc.text(`Page ${pageNum}`, pageWidth - margin, pageHeight - 12, { align: 'right' });
      }
    });

    // 5. Financial Summary Table (Right-aligned, yellow highlight)
    let finalY = (doc as any).lastAutoTable ? (doc as any).lastAutoTable.finalY + 12 : 100;
    if (finalY + 80 > pageHeight - 30) {
      doc.addPage();
      finalY = 30;
    }

    const summaryW = 180;
    const summaryX = pageWidth - margin - summaryW;
    const rowH = 14;

    const summaryRows = [
      { label: 'Total :', value: subtotal > 0 ? subtotal.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-', isYellow: true },
      { label: 'CGST @ 9%', value: cgst > 0 ? cgst.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-', isYellow: false },
      { label: 'SGST @ 9%', value: sgst > 0 ? sgst.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-', isYellow: false },
      { label: 'IGST @ 18%', value: '-', isYellow: false },
      { label: 'Grand Total :', value: grandTotal > 0 ? grandTotal.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-', isYellow: true }
    ];

    summaryRows.forEach((sr, idx) => {
      const curY = finalY + (idx * rowH);
      if (sr.isYellow) {
        doc.setFillColor(255, 255, 0); // Yellow
      } else {
        doc.setFillColor(255, 255, 255);
      }
      doc.rect(summaryX, curY, summaryW, rowH, 'FD');

      doc.setDrawColor(0, 0, 0);
      doc.setLineWidth(0.5);
      doc.rect(summaryX, curY, summaryW / 2, rowH, 'S');
      doc.rect(summaryX + summaryW / 2, curY, summaryW / 2, rowH, 'S');

      doc.setFont('helvetica', sr.isYellow ? 'bold' : 'normal');
      doc.setFontSize(7.5);
      doc.setTextColor(0, 0, 0);
      doc.text(sr.label, summaryX + 6, curY + 9.5);
      doc.text(sr.value, summaryX + summaryW - 6, curY + 9.5, { align: 'right' });
    });

    finalY += (summaryRows.length * rowH) + 12;

    // 6. Quotation Notes Box
    const notesW = pageWidth - (margin * 2);
    const notesText = (options.quotationNotes || '').trim();
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7);
    const splitNotes = notesText ? doc.splitTextToSize(notesText, notesW - 14) : [];
    const notesBoxH = Math.max(30, 14 + (splitNotes.length * 8.5) + 6);

    if (finalY + notesBoxH + 10 > pageHeight - 30) {
      doc.addPage();
      finalY = 30;
    }

    doc.setDrawColor(100, 116, 139);
    doc.setLineWidth(0.5);
    doc.rect(margin, finalY, notesW, notesBoxH, 'S');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7.5);
    doc.setTextColor(0, 32, 96);
    doc.text('Quotation Notes:', margin + 6, finalY + 11);

    if (splitNotes.length > 0) {
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(7);
      doc.setTextColor(30, 41, 59);
      doc.text(splitNotes, margin + 6, finalY + 22);
    }

    finalY += notesBoxH + 12;

    // 7. Terms & Conditions section below table
    if (finalY + 130 > pageHeight - 30) {
      doc.addPage();
      finalY = 32;
    }

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.setTextColor(0, 32, 96);
    doc.text('Terms & Conditions:', margin, finalY);

    doc.setDrawColor(0, 32, 96);
    doc.setLineWidth(0.8);
    doc.line(margin, finalY + 3, margin + 110, finalY + 3);

    finalY += 12;

    const termsAndConditions: string[] = this.getNgpTermsAndConditions(options.poPlacementAddress);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7);
    doc.setTextColor(30, 41, 59);

    const maxTextWidth = pageWidth - (margin * 2) - 15;

    termsAndConditions.forEach(term => {
      const splitText = doc.splitTextToSize(term, maxTextWidth);
      const textHeight = splitText.length * 8.5;

      if (finalY + textHeight > pageHeight - 25) {
        doc.addPage();
        finalY = 32;
      }

      // Small square bullet
      doc.setFillColor(0, 32, 96);
      doc.rect(margin + 2, finalY - 4, 2.5, 2.5, 'F');

      doc.text(splitText, margin + 10, finalY);
      finalY += textHeight + 2;
    });

    const pdfFileName = fileName.endsWith('.pdf') ? fileName : `${fileName}.pdf`;
    doc.save(pdfFileName);
  }

    private base64ToUint8Array(base64: string): Uint8Array {
    try {
      const cleanBase64 = base64.replace(/^data:image\/\w+;base64,/, '').trim();
      const binaryString = window.atob(cleanBase64);
      const len = binaryString.length;
      const bytes = new Uint8Array(len);
      for (let i = 0; i < len; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }
      return bytes;
    } catch (e) {
      console.warn('Failed to convert base64 to Uint8Array:', e);
      return new Uint8Array(0);
    }
  }

  /**
   * Standard NGP terms and conditions list
   */
  private getNgpTermsAndConditions(poPlacementAddress?: string): string[] {
    return [
      'GST will be applied at the time of billing.',
      'Costing in this quotation is prepared as per the existing customs tariff, duties, taxes, and statutory laws/notifications. Any changes in government notifications prior to delivery will impact the product cost.',
      'Any applicable law changes will affect the quoted product price.',
      'Warranty as per manufacturer.',
      'Please review MOQ, quoted UOM, part number, and lead time carefully before placing an order. Alternate product suggestions are based on market availability only; the buyer holds full responsibility for technical decisions.',
      'Distributor/Manufacturer COC will be provided with the supply of goods.',
      'All cables, braids, heat shrink, sleeves, fasteners, consumables, etc. are subject to ±10% variation or actuals at billing, depending on packing/spool conditions.',
      'For chemicals/DG goods, please check the shelf-life column carefully before ordering.',
      'All quotes and orders are NCNR – Non-Cancellable and Non-Returnable.',
      'All product parts are subject to stock availability.',
      'Any increase or decrease in quoted quantity will affect the unit cost.',
      'All parts are subject to 100% re-stocking charges.',
      'Prices are quoted based on current USD/EUR/GBP to INR conversion. Any fluctuation beyond 2% at the time of PO will impact the unit cost.',
      'Orders below ₹3,000/- will attract a delivery charge of ₹300/- in the invoice.',
      (poPlacementAddress && poPlacementAddress.trim()) ? `Purchase Orders must be placed in the name of:\n${poPlacementAddress.trim()}` : 'Purchase Orders must be placed in the name of:\nNGP SOLUTIONS, JR Urbania Prime, #969 & 970, Lingapura Village, Anekal Taluk, Bangalore – 562106 | GST : 29BCWPD5012J1ZC',
      'Payment Terms : Terms will vary depending on the customer.',
      'Generally, the company approves Net 30 terms subject to finance approval.',
      'As per company policy, for any new customers, 100% advance payment is required until either: The transaction value reaches ₹10 lakhs, or The customer submits 2 years of audited financial statements for review and approval of credit terms.'
    ];
  }

  /**
   * Export Supply Sense data as custom NGP Quotation Form in Word (.docx) format matching the quotation template
   */
  async exportToNgpQuotationDocx(
    rows: any[],
    fileName: string = 'NGP_Quotation.docx',
    options: {
      reviewerName?: string;
      dateStr?: string;
      projectName?: string;
      customerName?: string;
      customerGst?: string;
      salutationName?: string;
      buyerName?: string;
      buyerAddress?: string;
      buyerEmail?: string;
      buyerPhone?: string;
      quotationNo?: string;
      validUntilStr?: string;
      sellerCompanyName?: string;
      sellerAddress?: string;
      sellerGst?: string;
      sellerPhone?: string;
      sellerEmail?: string;
      salesAgent?: string;
      poPlacementAddress?: string;
      quotationNotes?: string;
      footerCompanyName?: string;
      footerAddress?: string;
    } = {}
  ): Promise<void> {
    if (!rows || !rows.length) {
      console.warn('No data available to export.');
      return;
    }

    const reviewerName = (options.reviewerName || 'KAVYA R').trim().toUpperCase();
    const now = new Date();
    const dateStr = options.dateStr || (() => {
      const dd = String(now.getDate()).padStart(2, '0');
      const mm = String(now.getMonth() + 1).padStart(2, '0');
      const yyyy = now.getFullYear();
      return `${dd}-${mm}-${yyyy}`;
    })();

    const validUntilDate = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);
    const validUntilStr = (() => {
      const dd = String(validUntilDate.getDate()).padStart(2, '0');
      const mm = String(validUntilDate.getMonth() + 1).padStart(2, '0');
      const yyyy = validUntilDate.getFullYear();
      return `${dd}-${mm}-${yyyy}`;
    })();

    const quotationNo = options.quotationNo || `NGP/QTN/${now.getFullYear()}/${Math.floor(100000 + Math.random() * 900000)}`;
    const projectName = options.projectName || '';
    const customerName = (options.customerName || projectName || '').trim();
    const customerGst = (options.customerGst || '').trim();
    const salutationName = (options.salutationName || options.buyerName || '').trim();
    const buyerName = (options.buyerName || '').trim();
    const buyerAddress = (options.buyerAddress || '').trim();
    const buyerEmail = (options.buyerEmail || '').trim();
    const buyerPhone = (options.buyerPhone || '').trim();

    const sellerCompanyName = (options.sellerCompanyName || 'NGP SOLUTIONS').trim();
    const sellerAddress = (options.sellerAddress || 'JR URBANIA PRIME, # 969 & 970, LINGAPURA VILLAGE, ANEKAL TALUK, BANGALORE – 562106').trim();
    const sellerGst = (options.sellerGst || '29BCWPD5012J1ZC').trim();
    const sellerEmail = (options.sellerEmail || 'kavya.r@vigroup.in').trim();
    const sellerPhone = (options.sellerPhone || '+91 9606048420').trim();
    const salesAgent = (options.salesAgent || reviewerName).trim();

    let subtotal = 0;
    rows.forEach(r => {
      const qty = Number(r.quantity ?? r.projectQty ?? 0);
      const unitPrice = Number(r.unitPrice ?? 0);
      const rowTotal = (r.totalPrice !== undefined && r.totalPrice !== null && !isNaN(Number(r.totalPrice)))
        ? Number(r.totalPrice)
        : (qty * unitPrice);
      subtotal += rowTotal;
    });

    const cgst = subtotal * 0.09;
    const sgst = subtotal * 0.09;
    const grandTotal = subtotal + cgst + sgst;

    const logoBytes = this.base64ToUint8Array(NGP_LOGO_BASE64);

    // Header Table (Logo + Metadata Box) on every page
    const headerTable = new Table({
      layout: TableLayoutType.FIXED,
      width: { size: 11006, type: WidthType.DXA },
      columnWidths: [6006, 5000],
      borders: {
        top: { style: BorderStyle.NONE },
        bottom: { style: BorderStyle.NONE },
        left: { style: BorderStyle.NONE },
        right: { style: BorderStyle.NONE },
        insideHorizontal: { style: BorderStyle.NONE },
        insideVertical: { style: BorderStyle.NONE }
      },
      rows: [
        new TableRow({
          cantSplit: true,
          children: [
            // Left Cell: Logo
            new TableCell({
              width: { size: 6006, type: WidthType.DXA },
              borders: {
                top: { style: BorderStyle.NONE },
                bottom: { style: BorderStyle.NONE },
                left: { style: BorderStyle.NONE },
                right: { style: BorderStyle.NONE }
              },
              children: [
                new Paragraph({
                  children: logoBytes.length > 0 ? [
                    new ImageRun({
                      data: logoBytes,
                      transformation: { width: 140, height: 75 },
                      type: 'jpg'
                    })
                  ] : [
                    new TextRun({ text: 'NGP SOLUTIONS', bold: true, size: 28, color: '002060', font: 'Calibri' })
                  ]
                })
              ]
            }),
            // Right Cell: Metadata Box with QUOTATION Title
            new TableCell({
              width: { size: 5000, type: WidthType.DXA },
              borders: {
                top: { style: BorderStyle.SINGLE, size: 1, color: '64748B' },
                bottom: { style: BorderStyle.SINGLE, size: 1, color: '64748B' },
                left: { style: BorderStyle.SINGLE, size: 1, color: '64748B' },
                right: { style: BorderStyle.SINGLE, size: 1, color: '64748B' }
              },
              margins: { top: 50, bottom: 50, left: 80, right: 80 },
              children: [
                new Paragraph({
                  spacing: { after: 15 },
                  children: [
                    new TextRun({ text: 'Doc. Reference : ', bold: true, size: 15, font: 'Calibri', color: '475569' }),
                    new TextRun({ text: 'NGP-MKT-QF-02', size: 15, font: 'Calibri', color: '1E293B' })
                  ]
                }),
                new Paragraph({
                  spacing: { after: 15 },
                  children: [
                    new TextRun({ text: 'Rev            : ', bold: true, size: 15, font: 'Calibri', color: '475569' }),
                    new TextRun({ text: '02', size: 15, font: 'Calibri', color: '1E293B' })
                  ]
                }),
                new Paragraph({
                  spacing: { after: 40 },
                  children: [
                    new TextRun({ text: 'Date           : ', bold: true, size: 15, font: 'Calibri', color: '475569' }),
                    new TextRun({ text: dateStr, size: 15, font: 'Calibri', color: '1E293B' })
                  ]
                }),
                new Paragraph({
                  alignment: AlignmentType.CENTER,
                  border: { top: { style: BorderStyle.SINGLE, size: 1, color: '94A3B8' } },
                  spacing: { before: 40, after: 15 },
                  children: [
                    new TextRun({ text: 'QUOTATION', bold: true, size: 24, font: 'Calibri', color: '334155' })
                  ]
                })
              ]
            })
          ]
        })
      ]
    });

    // Footer Table (Company info + Page number)
    const footerTable = new Table({
      layout: TableLayoutType.FIXED,
      width: { size: 11006, type: WidthType.DXA },
      columnWidths: [9356, 1650],
      borders: {
        top: { style: BorderStyle.NONE },
        bottom: { style: BorderStyle.NONE },
        left: { style: BorderStyle.NONE },
        right: { style: BorderStyle.NONE },
        insideHorizontal: { style: BorderStyle.NONE },
        insideVertical: { style: BorderStyle.NONE }
      },
      rows: [
        new TableRow({
          cantSplit: true,
          children: [
            new TableCell({
              width: { size: 9356, type: WidthType.DXA },
              borders: {
                top: { style: BorderStyle.NONE },
                bottom: { style: BorderStyle.NONE },
                left: { style: BorderStyle.NONE },
                right: { style: BorderStyle.NONE }
              },
              children: [
                new Paragraph({
                  children: [
                    new TextRun({ text: 'NGP SOLUTIONS', bold: true, size: 14, font: 'Calibri', color: '1E293B' })
                  ]
                }),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: 'JR URBANIA PRIME, # 969 & 970, LINGAPURA VILLAGE, ANEKAL TALUK, BANGALORE – 562106 | Email: sales@vigroup.in | Ph# 702617646',
                      size: 12,
                      font: 'Calibri',
                      color: '64748B'
                    })
                  ]
                })
              ]
            }),
            new TableCell({
              width: { size: 1650, type: WidthType.DXA },
              borders: {
                top: { style: BorderStyle.NONE },
                bottom: { style: BorderStyle.NONE },
                left: { style: BorderStyle.NONE },
                right: { style: BorderStyle.NONE }
              },
              children: [
                new Paragraph({
                  alignment: AlignmentType.RIGHT,
                  children: [
                    new TextRun({ text: 'Page ', size: 14, font: 'Calibri', color: '64748B' }),
                    new TextRun({ children: [PageNumber.CURRENT], size: 14, font: 'Calibri', color: '64748B' })
                  ]
                })
              ]
            })
          ]
        })
      ]
    });

    // 1. Blue Bar Table
    const blueBarTable = new Table({
      layout: TableLayoutType.FIXED,
      width: { size: 11006, type: WidthType.DXA },
      columnWidths: [4400, 3300, 3306],
      borders: {
        top: { style: BorderStyle.NONE },
        bottom: { style: BorderStyle.NONE },
        left: { style: BorderStyle.NONE },
        right: { style: BorderStyle.NONE },
        insideHorizontal: { style: BorderStyle.NONE },
        insideVertical: { style: BorderStyle.NONE }
      },
      rows: [
        new TableRow({
          cantSplit: true,
          children: [
            new TableCell({
              width: { size: 4400, type: WidthType.DXA },
              shading: { fill: '002060' },
              margins: { top: 60, bottom: 60, left: 80, right: 80 },
              borders: {
                top: { style: BorderStyle.NONE },
                bottom: { style: BorderStyle.NONE },
                left: { style: BorderStyle.NONE },
                right: { style: BorderStyle.NONE }
              },
              children: [
                new Paragraph({
                  children: [
                    new TextRun({ text: `QUOTATION # ${quotationNo}`, bold: true, size: 15, color: 'FFFFFF', font: 'Calibri' })
                  ]
                })
              ]
            }),
            new TableCell({
              width: { size: 3300, type: WidthType.DXA },
              shading: { fill: '002060' },
              margins: { top: 60, bottom: 60, left: 80, right: 80 },
              borders: {
                top: { style: BorderStyle.NONE },
                bottom: { style: BorderStyle.NONE },
                left: { style: BorderStyle.NONE },
                right: { style: BorderStyle.NONE }
              },
              children: [
                new Paragraph({
                  alignment: AlignmentType.CENTER,
                  children: [
                    new TextRun({ text: `DATE : ${dateStr}`, bold: true, size: 15, color: 'FFFFFF', font: 'Calibri' })
                  ]
                })
              ]
            }),
            new TableCell({
              width: { size: 3306, type: WidthType.DXA },
              shading: { fill: '002060' },
              margins: { top: 60, bottom: 60, left: 80, right: 80 },
              borders: {
                top: { style: BorderStyle.NONE },
                bottom: { style: BorderStyle.NONE },
                left: { style: BorderStyle.NONE },
                right: { style: BorderStyle.NONE }
              },
              children: [
                new Paragraph({
                  alignment: AlignmentType.RIGHT,
                  children: [
                    new TextRun({ text: `QUOTE VALID : ${validUntilStr}`, bold: true, size: 15, color: 'FFFFFF', font: 'Calibri' })
                  ]
                })
              ]
            })
          ]
        })
      ]
    });

    // 2. Customer & Sales Agent Info Table (2 Columns)
    const detailsTable = new Table({
      layout: TableLayoutType.FIXED,
      width: { size: 11006, type: WidthType.DXA },
      columnWidths: [5503, 5503],
      borders: {
        top: { style: BorderStyle.NONE },
        bottom: { style: BorderStyle.NONE },
        left: { style: BorderStyle.NONE },
        right: { style: BorderStyle.NONE },
        insideHorizontal: { style: BorderStyle.NONE },
        insideVertical: { style: BorderStyle.NONE }
      },
      rows: [
        new TableRow({
          cantSplit: true,
          children: [
            // Left Column: Customer Details & Address (matching Quote_Template_1)
            new TableCell({
              width: { size: 5503, type: WidthType.DXA },
              borders: {
                top: { style: BorderStyle.SINGLE, size: 1, color: '64748B' },
                bottom: { style: BorderStyle.SINGLE, size: 1, color: '64748B' },
                left: { style: BorderStyle.SINGLE, size: 1, color: '64748B' },
                right: { style: BorderStyle.SINGLE, size: 1, color: '64748B' }
              },
              margins: { top: 80, bottom: 80, left: 100, right: 100 },
              children: (() => {
                const customerParagraphs: Paragraph[] = [
                  new Paragraph({
                    spacing: { after: 30 },
                    children: [
                      new TextRun({
                        text: (customerName || 'CUSTOMER').toUpperCase(),
                        bold: true,
                        size: 26,
                        color: '002060',
                        font: 'Calibri'
                      })
                    ]
                  })
                ];

                const addrText = (buyerAddress && buyerAddress.trim()) ? buyerAddress.trim() : '';
                if (addrText) {
                  const addrLines = addrText.split('\n');
                  addrLines.forEach((line, idx) => {
                    if (line.trim()) {
                      customerParagraphs.push(
                        new Paragraph({
                          spacing: { after: idx === addrLines.length - 1 ? 30 : 15 },
                          children: [
                            new TextRun({
                              text: line.trim(),
                              size: 20,
                              color: '334155',
                              font: 'Calibri'
                            })
                          ]
                        })
                      );
                    }
                  });
                } else {
                  customerParagraphs.push(
                    new Paragraph({
                      spacing: { after: 30 },
                      children: [
                        new TextRun({
                          text: 'Address: Available on request',
                          size: 20,
                          color: '64748B',
                          font: 'Calibri'
                        })
                      ]
                    })
                  );
                }

                if (customerGst) {
                  customerParagraphs.push(
                    new Paragraph({
                      spacing: { before: 20 },
                      children: [
                        new TextRun({
                          text: customerGst.toUpperCase().startsWith('GST') ? customerGst : `GST: ${customerGst}`,
                          bold: true,
                          size: 22,
                          color: '0F172A',
                          font: 'Calibri'
                        })
                      ]
                    })
                  );
                }

                return customerParagraphs;
              })()
            }),
            // Right Column: Sales Agent & Customer Contact Details
            new TableCell({
              width: { size: 5503, type: WidthType.DXA },
              borders: {
                top: { style: BorderStyle.SINGLE, size: 1, color: '64748B' },
                bottom: { style: BorderStyle.SINGLE, size: 1, color: '64748B' },
                left: { style: BorderStyle.SINGLE, size: 1, color: '64748B' },
                right: { style: BorderStyle.SINGLE, size: 1, color: '64748B' }
              },
              margins: { top: 80, bottom: 80, left: 100, right: 100 },
              children: [
                new Paragraph({
                  spacing: { after: 15 },
                  children: [
                    new TextRun({ text: 'YOUR SALES AGENT : ', bold: true, size: 18, font: 'Calibri', color: '1E293B' }),
                    new TextRun({ text: salesAgent || reviewerName, size: 18, font: 'Calibri', color: '002060', bold: true })
                  ]
                }),
                new Paragraph({
                  spacing: { after: 15 },
                  children: [
                    new TextRun({ text: 'EMAIL            : ', bold: true, size: 18, font: 'Calibri', color: '1E293B' }),
                    new TextRun({ text: sellerEmail, size: 18, font: 'Calibri', color: '2563EB', underline: {} })
                  ]
                }),
                new Paragraph({
                  spacing: { after: 35 },
                  children: [
                    new TextRun({ text: 'PHONE NO.        : ', bold: true, size: 18, font: 'Calibri', color: '1E293B' }),
                    new TextRun({ text: sellerPhone, size: 18, font: 'Calibri', color: '1E293B' })
                  ]
                }),
                new Paragraph({
                  border: { top: { style: BorderStyle.SINGLE, size: 1, color: 'CBD5E1' } },
                  spacing: { before: 25, after: 15 },
                  children: [
                    new TextRun({ text: 'CUSTOMER REFERENCE : ', bold: true, size: 18, font: 'Calibri', color: '475569' }),
                    new TextRun({ text: customerName || projectName, size: 18, font: 'Calibri', color: '1E293B' })
                  ]
                }),
                new Paragraph({
                  spacing: { after: 15 },
                  children: [
                    new TextRun({ text: 'BUYER              : ', bold: true, size: 18, font: 'Calibri', color: '475569' }),
                    new TextRun({ text: buyerName || '-', size: 18, font: 'Calibri', color: '1E293B' })
                  ]
                }),
                new Paragraph({
                  spacing: { after: 15 },
                  children: [
                    new TextRun({ text: 'EMAIL ID           : ', bold: true, size: 18, font: 'Calibri', color: '475569' }),
                    new TextRun({ text: buyerEmail || '-', size: 18, font: 'Calibri', color: '1E293B' })
                  ]
                }),
                new Paragraph({
                  children: [
                    new TextRun({ text: 'PHONE NUMBER       : ', bold: true, size: 18, font: 'Calibri', color: '475569' }),
                    new TextRun({ text: buyerPhone || '-', size: 18, font: 'Calibri', color: '1E293B' })
                  ]
                })
              ]
            })
          ]
        })
      ]
    });

    // 3. Salutation Paragraph with Buyer Name (matching Quote_Template_1)
    const buyerDisplayName = (buyerName && buyerName.trim()) || (salutationName && salutationName.trim()) || '__________________________';
    const introParagraph = new Paragraph({
      spacing: { before: 140, after: 140 },
      children: [
        new TextRun({
          text: `Dear Mr. ${buyerDisplayName}, Thank you for your valuable RFQ and the opportunity to participate in your sourcing requirements. Based on your demands and specifications, we have carefully reviewed the requirements and worked out our most competitive pricing for your kind review and consideration. We look forward to your valuable feedback and the opportunity to support your business requirements.`,
          size: 20,
          font: 'Calibri',
          color: '1E293B'
        })
      ]
    });

    // 4. Main Quotation Table (15 Columns - Exact Fixed Dimensions for A4 Printout)
    const headers = [
      { lines: ['SL', 'NO'], width: 380, align: AlignmentType.CENTER },
      { lines: ['CUSTOMER', 'REFERENCE'], width: 1050, align: AlignmentType.LEFT },
      { lines: ['MANUFACTURER', 'PART NO.'], width: 1250, align: AlignmentType.LEFT },
      { lines: ['ALTERNATE', 'QUOTED'], width: 1050, align: AlignmentType.LEFT },
      { lines: ['DESCRIPTION'], width: 1350, align: AlignmentType.LEFT },
      { lines: ['MANUFACTURER'], width: 1050, align: AlignmentType.LEFT },
      { lines: ['HSN', 'CODE'], width: 550, align: AlignmentType.CENTER },
      { lines: ['UOM'], width: 400, align: AlignmentType.CENTER },
      { lines: ['QTY'], width: 500, align: AlignmentType.RIGHT },
      { lines: ['MOQ/SPQ'], width: 550, align: AlignmentType.CENTER },
      { lines: ['UNIT', 'PRICE'], width: 720, align: AlignmentType.RIGHT },
      { lines: ['TOTAL'], width: 780, align: AlignmentType.RIGHT },
      { lines: ['LEAD', 'TIME'], width: 550, align: AlignmentType.CENTER },
      { lines: ['COO'], width: 400, align: AlignmentType.CENTER },
      { lines: ['REMARKS'], width: 456, align: AlignmentType.LEFT }
    ];

    const tableHeaderRow = new TableRow({
      tableHeader: true,
      cantSplit: true,
      children: headers.map(h => new TableCell({
        width: { size: h.width, type: WidthType.DXA },
        shading: { fill: '002060' },
        verticalAlign: VerticalAlign.CENTER,
        margins: { top: 40, bottom: 40, left: 15, right: 15 },
        borders: {
          top: { style: BorderStyle.SINGLE, size: 1, color: '001040' },
          bottom: { style: BorderStyle.SINGLE, size: 1, color: '001040' },
          left: { style: BorderStyle.SINGLE, size: 1, color: '001040' },
          right: { style: BorderStyle.SINGLE, size: 1, color: '001040' }
        },
        children: h.lines.map(line => new Paragraph({
          alignment: AlignmentType.CENTER,
          spacing: { before: 0, after: 0, line: 190 },
          children: [
            new TextRun({ text: line, bold: true, size: 13, font: 'Calibri', color: 'FFFFFF' })
          ]
        }))
      }))
    });

    const tableDataRows = rows.map((r, idx) => {
      const qty = Number(r.quantity ?? r.projectQty ?? 0);
      const unitPrice = Number(r.unitPrice ?? 0);
      const total = (r.totalPrice !== undefined && r.totalPrice !== null && !isNaN(Number(r.totalPrice)))
        ? Number(r.totalPrice)
        : (qty * unitPrice);

      const cellValues = [
        { text: String(r.slNo ?? (idx + 1)), align: AlignmentType.CENTER },
        { text: String(r.partNumber ?? ''), align: AlignmentType.LEFT },
        { text: String(r.quotedPart ?? ''), align: AlignmentType.LEFT },
        { text: String(r.alternateQuoted ?? ''), align: AlignmentType.LEFT },
        { text: String(r.description ?? ''), align: AlignmentType.LEFT },
        { text: String(r.make ?? ''), align: AlignmentType.LEFT },
        { text: String(r.hsnCode ?? ''), align: AlignmentType.CENTER },
        { text: String(r.uom ?? 'EA'), align: AlignmentType.CENTER },
        { text: qty ? qty.toLocaleString('en-IN') : '0', align: AlignmentType.RIGHT },
        { text: String(r.moqSpq ?? ''), align: AlignmentType.CENTER },
        { text: unitPrice > 0 ? unitPrice.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-', align: AlignmentType.RIGHT },
        { text: total > 0 ? total.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-', align: AlignmentType.RIGHT },
        { text: String(r.leadTime ?? ''), align: AlignmentType.CENTER },
        { text: String(r.coo ?? ''), align: AlignmentType.CENTER },
        { text: String(r.remarks ?? ''), align: AlignmentType.LEFT }
      ];

      return new TableRow({
        cantSplit: true,
        children: cellValues.map((cv, cIdx) => new TableCell({
          width: { size: headers[cIdx].width, type: WidthType.DXA },
          verticalAlign: VerticalAlign.CENTER,
          margins: { top: 30, bottom: 30, left: 15, right: 15 },
          borders: {
            top: { style: BorderStyle.SINGLE, size: 1, color: 'CBD5E1' },
            bottom: { style: BorderStyle.SINGLE, size: 1, color: 'CBD5E1' },
            left: { style: BorderStyle.SINGLE, size: 1, color: 'CBD5E1' },
            right: { style: BorderStyle.SINGLE, size: 1, color: 'CBD5E1' }
          },
          children: [
            new Paragraph({
              alignment: cv.align,
              spacing: { before: 0, after: 0, line: 200 },
              children: [
                new TextRun({ text: cv.text || '-', size: 13, font: 'Calibri', color: '000000' })
              ]
            })
          ]
        }))
      });
    });

    const quotationItemsTable = new Table({
      layout: TableLayoutType.FIXED,
      width: { size: 11006, type: WidthType.DXA },
      columnWidths: headers.map(h => h.width),
      rows: [tableHeaderRow, ...tableDataRows]
    });

    // 5. Financial Summary Table (Right-aligned, yellow highlight, fixed layout)
    const summaryBorder = { style: BorderStyle.SINGLE, size: 1, color: '000000' };
    const noBorder = { style: BorderStyle.NONE };

    const summaryTable = new Table({
      layout: TableLayoutType.FIXED,
      width: { size: 11006, type: WidthType.DXA },
      columnWidths: [7606, 1700, 1700],
      borders: {
        top: noBorder,
        bottom: noBorder,
        left: noBorder,
        right: noBorder,
        insideHorizontal: noBorder,
        insideVertical: noBorder
      },
      rows: [
        new TableRow({
          cantSplit: true,
          children: [
            new TableCell({
              width: { size: 7606, type: WidthType.DXA },
              borders: { top: noBorder, bottom: noBorder, left: noBorder, right: noBorder },
              children: [new Paragraph({ spacing: { before: 0, after: 0 } })]
            }),
            new TableCell({
              width: { size: 1700, type: WidthType.DXA },
              shading: { fill: 'FFFF00' },
              margins: { top: 35, bottom: 35, left: 50, right: 50 },
              borders: { top: summaryBorder, bottom: summaryBorder, left: summaryBorder, right: summaryBorder },
              children: [new Paragraph({ spacing: { before: 0, after: 0 }, children: [new TextRun({ text: 'Total :', bold: true, size: 16, font: 'Calibri', color: '000000' })] })]
            }),
            new TableCell({
              width: { size: 1700, type: WidthType.DXA },
              shading: { fill: 'FFFF00' },
              margins: { top: 35, bottom: 35, left: 50, right: 50 },
              borders: { top: summaryBorder, bottom: summaryBorder, left: summaryBorder, right: summaryBorder },
              children: [new Paragraph({ alignment: AlignmentType.RIGHT, spacing: { before: 0, after: 0 }, children: [new TextRun({ text: subtotal > 0 ? subtotal.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-', bold: true, size: 16, font: 'Calibri', color: '000000' })] })]
            })
          ]
        }),
        new TableRow({
          cantSplit: true,
          children: [
            new TableCell({
              width: { size: 7606, type: WidthType.DXA },
              borders: { top: noBorder, bottom: noBorder, left: noBorder, right: noBorder },
              children: [new Paragraph({ spacing: { before: 0, after: 0 } })]
            }),
            new TableCell({
              width: { size: 1700, type: WidthType.DXA },
              margins: { top: 35, bottom: 35, left: 50, right: 50 },
              borders: { top: summaryBorder, bottom: summaryBorder, left: summaryBorder, right: summaryBorder },
              children: [new Paragraph({ spacing: { before: 0, after: 0 }, children: [new TextRun({ text: 'CGST @ 9%', size: 15, font: 'Calibri', color: '000000' })] })]
            }),
            new TableCell({
              width: { size: 1700, type: WidthType.DXA },
              margins: { top: 35, bottom: 35, left: 50, right: 50 },
              borders: { top: summaryBorder, bottom: summaryBorder, left: summaryBorder, right: summaryBorder },
              children: [new Paragraph({ alignment: AlignmentType.RIGHT, spacing: { before: 0, after: 0 }, children: [new TextRun({ text: cgst > 0 ? cgst.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-', size: 15, font: 'Calibri', color: '000000' })] })]
            })
          ]
        }),
        new TableRow({
          cantSplit: true,
          children: [
            new TableCell({
              width: { size: 7606, type: WidthType.DXA },
              borders: { top: noBorder, bottom: noBorder, left: noBorder, right: noBorder },
              children: [new Paragraph({ spacing: { before: 0, after: 0 } })]
            }),
            new TableCell({
              width: { size: 1700, type: WidthType.DXA },
              margins: { top: 35, bottom: 35, left: 50, right: 50 },
              borders: { top: summaryBorder, bottom: summaryBorder, left: summaryBorder, right: summaryBorder },
              children: [new Paragraph({ spacing: { before: 0, after: 0 }, children: [new TextRun({ text: 'SGST @ 9%', size: 15, font: 'Calibri', color: '000000' })] })]
            }),
            new TableCell({
              width: { size: 1700, type: WidthType.DXA },
              margins: { top: 35, bottom: 35, left: 50, right: 50 },
              borders: { top: summaryBorder, bottom: summaryBorder, left: summaryBorder, right: summaryBorder },
              children: [new Paragraph({ alignment: AlignmentType.RIGHT, spacing: { before: 0, after: 0 }, children: [new TextRun({ text: sgst > 0 ? sgst.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-', size: 15, font: 'Calibri', color: '000000' })] })]
            })
          ]
        }),
        new TableRow({
          cantSplit: true,
          children: [
            new TableCell({
              width: { size: 7606, type: WidthType.DXA },
              borders: { top: noBorder, bottom: noBorder, left: noBorder, right: noBorder },
              children: [new Paragraph({ spacing: { before: 0, after: 0 } })]
            }),
            new TableCell({
              width: { size: 1700, type: WidthType.DXA },
              margins: { top: 35, bottom: 35, left: 50, right: 50 },
              borders: { top: summaryBorder, bottom: summaryBorder, left: summaryBorder, right: summaryBorder },
              children: [new Paragraph({ spacing: { before: 0, after: 0 }, children: [new TextRun({ text: 'IGST @ 18%', size: 15, font: 'Calibri', color: '000000' })] })]
            }),
            new TableCell({
              width: { size: 1700, type: WidthType.DXA },
              margins: { top: 35, bottom: 35, left: 50, right: 50 },
              borders: { top: summaryBorder, bottom: summaryBorder, left: summaryBorder, right: summaryBorder },
              children: [new Paragraph({ alignment: AlignmentType.RIGHT, spacing: { before: 0, after: 0 }, children: [new TextRun({ text: '-', size: 15, font: 'Calibri', color: '000000' })] })]
            })
          ]
        }),
        new TableRow({
          cantSplit: true,
          children: [
            new TableCell({
              width: { size: 7606, type: WidthType.DXA },
              borders: { top: noBorder, bottom: noBorder, left: noBorder, right: noBorder },
              children: [new Paragraph({ spacing: { before: 0, after: 0 } })]
            }),
            new TableCell({
              width: { size: 1700, type: WidthType.DXA },
              shading: { fill: 'FFFF00' },
              margins: { top: 35, bottom: 35, left: 50, right: 50 },
              borders: { top: summaryBorder, bottom: summaryBorder, left: summaryBorder, right: summaryBorder },
              children: [new Paragraph({ spacing: { before: 0, after: 0 }, children: [new TextRun({ text: 'Grand Total :', bold: true, size: 17, font: 'Calibri', color: '000000' })] })]
            }),
            new TableCell({
              width: { size: 1700, type: WidthType.DXA },
              shading: { fill: 'FFFF00' },
              margins: { top: 35, bottom: 35, left: 50, right: 50 },
              borders: { top: summaryBorder, bottom: summaryBorder, left: summaryBorder, right: summaryBorder },
              children: [new Paragraph({ alignment: AlignmentType.RIGHT, spacing: { before: 0, after: 0 }, children: [new TextRun({ text: grandTotal > 0 ? grandTotal.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-', bold: true, size: 17, font: 'Calibri', color: '000000' })] })]
            })
          ]
        })
      ]
    });

    // 6. Quotation Notes Box (Print User Entered Notes)
    const notesChildren: Paragraph[] = [
      new Paragraph({
        spacing: { after: 30 },
        children: [
          new TextRun({ text: 'Quotation Notes:', bold: true, size: 18, font: 'Calibri', color: '002060', underline: {} })
        ]
      })
    ];

    const notesContent = (options.quotationNotes || '').trim();
    if (notesContent) {
      const noteLines = notesContent.split('\n');
      noteLines.forEach(nl => {
        if (nl.trim()) {
          notesChildren.push(
            new Paragraph({
              spacing: { before: 15, after: 15 },
              children: [
                new TextRun({ text: nl.trim(), size: 17, font: 'Calibri', color: '1E293B' })
              ]
            })
          );
        }
      });
    } else {
      notesChildren.push(
        new Paragraph({
          children: [
            new TextRun({ text: 'No additional quotation notes.', size: 16, font: 'Calibri', color: '64748B', italics: true })
          ]
        })
      );
    }

    const quotationNotesBox = new Table({
      layout: TableLayoutType.FIXED,
      width: { size: 11006, type: WidthType.DXA },
      columnWidths: [11006],
      borders: {
        top: { style: BorderStyle.SINGLE, size: 1, color: '64748B' },
        bottom: { style: BorderStyle.SINGLE, size: 1, color: '64748B' },
        left: { style: BorderStyle.SINGLE, size: 1, color: '64748B' },
        right: { style: BorderStyle.SINGLE, size: 1, color: '64748B' }
      },
      rows: [
        new TableRow({
          cantSplit: true,
          children: [
            new TableCell({
              width: { size: 11006, type: WidthType.DXA },
              margins: { top: 80, bottom: 80, left: 100, right: 100 },
              children: notesChildren
            })
          ]
        })
      ]
    });

    // 7. Terms & Conditions
    const terms = this.getNgpTermsAndConditions(options.poPlacementAddress);
    const termsParagraphs = [
      new Paragraph({
        pageBreakBefore: true,
        spacing: { before: 80, after: 40 },
        children: [
          new TextRun({ text: 'Delivery Terms:', bold: true, size: 16, font: 'Calibri', color: '000000' })
        ]
      }),
      new Paragraph({
        spacing: { after: 80 },
        children: [
          new TextRun({ text: 'Ex-Works / Standard Delivery Schedule as quoted.', size: 14, font: 'Calibri', color: '334155' })
        ]
      }),
      new Paragraph({
        spacing: { before: 60, after: 40 },
        children: [
          new TextRun({ text: 'Payment Terms:', bold: true, size: 16, font: 'Calibri', color: '000000' })
        ]
      }),
      new Paragraph({
        spacing: { after: 100 },
        children: [
          new TextRun({ text: 'Net 30 days subject to finance approval / 100% advance for new customers.', size: 14, font: 'Calibri', color: '334155' })
        ]
      }),
      new Paragraph({
        spacing: { before: 80, after: 60 },
        children: [
          new TextRun({ text: 'General Terms & Conditions:', bold: true, size: 17, font: 'Calibri', color: '000000', underline: {} })
        ]
      }),
      ...terms.map(term => new Paragraph({
        spacing: { after: 20 },
        children: [
          new TextRun({ text: '▪  ', bold: true, size: 13, font: 'Calibri', color: '002060' }),
          new TextRun({ text: term, size: 14, font: 'Calibri', color: '1E293B' })
        ]
      })),
      new Paragraph({
        spacing: { before: 120, after: 30 },
        children: [
          new TextRun({ text: 'For,', size: 15, font: 'Calibri', color: '1E293B' })
        ]
      }),
      new Paragraph({
        spacing: { after: 280 },
        children: [
          new TextRun({ text: 'NGP SOLUTIONS', bold: true, size: 17, font: 'Calibri', color: '002060' })
        ]
      }),
      new Paragraph({
        spacing: { after: 80 },
        children: [
          new TextRun({ text: 'Authorized Signatories', bold: true, size: 16, font: 'Calibri', color: '000000' })
        ]
      }),
      new Paragraph({
        spacing: { before: 80, after: 40 },
        children: [
          new TextRun({
            text: 'This is a system generated document and does not require manual signatories',
            italics: true,
            size: 14,
            font: 'Calibri',
            color: '64748B'
          })
        ]
      })
    ];

    // Build Word Document
    const doc = new Document({
      sections: [
        {
          properties: {
            page: {
              size: {
                orientation: PageOrientation.PORTRAIT
              },
              margin: {
                top: 450,
                bottom: 450,
                left: 450,
                right: 450
              }
            }
          },
          headers: {
            default: new Header({
              children: [headerTable]
            })
          },
          footers: {
            default: new Footer({
              children: [footerTable]
            })
          },
          children: [
            blueBarTable,
            new Paragraph({ spacing: { after: 60 } }),
            detailsTable,
            introParagraph,
            quotationItemsTable,
            new Paragraph({ spacing: { after: 60 } }),
            summaryTable,
            new Paragraph({ spacing: { after: 80 } }),
            quotationNotesBox,
            ...termsParagraphs
          ]
        }
      ]
    });

    const blob = await Packer.toBlob(doc);
    saveAs(blob, fileName.endsWith('.docx') ? fileName : `${fileName}.docx`);
  }

  /**
   * Export general data to standard Word (.docx) document for other instances
   */
  async exportToDocx(
    data: any[],
    fileName: string = 'ExportedData.docx',
    title: string = 'Supply Sense Report'
  ): Promise<void> {
    if (!data || !data.length) {
      console.warn('No data available to export.');
      return;
    }

    const headers = Object.keys(data[0]);

    // Build header row
    const headerRow = new TableRow({
      tableHeader: true,
      cantSplit: true,
      children: headers.map(h => new TableCell({
        shading: { fill: '2563EB' },
        verticalAlign: VerticalAlign.CENTER,
        margins: { top: 50, bottom: 50, left: 30, right: 30 },
        borders: {
          top: { style: BorderStyle.SINGLE, size: 1, color: '93C5FD' },
          bottom: { style: BorderStyle.SINGLE, size: 1, color: '93C5FD' },
          left: { style: BorderStyle.SINGLE, size: 1, color: '93C5FD' },
          right: { style: BorderStyle.SINGLE, size: 1, color: '93C5FD' }
        },
        children: [
          new Paragraph({
            alignment: AlignmentType.CENTER,
            spacing: { before: 0, after: 0 },
            children: [
              new TextRun({ text: h, bold: true, size: 13, font: 'Calibri', color: 'FFFFFF' })
            ]
          })
        ]
      }))
    });

    // Build data rows
    const dataRows = data.map((item, idx) => new TableRow({
      cantSplit: true,
      children: headers.map(h => {
        const val = item[h] !== undefined && item[h] !== null ? String(item[h]) : '';
        const isNum = !isNaN(Number(val)) && val.trim() !== '';
        return new TableCell({
          shading: { fill: idx % 2 === 0 ? 'FFFFFF' : 'F8FAFC' },
          verticalAlign: VerticalAlign.CENTER,
          margins: { top: 30, bottom: 30, left: 25, right: 25 },
          borders: {
            top: { style: BorderStyle.SINGLE, size: 1, color: 'E2E8F0' },
            bottom: { style: BorderStyle.SINGLE, size: 1, color: 'E2E8F0' },
            left: { style: BorderStyle.SINGLE, size: 1, color: 'E2E8F0' },
            right: { style: BorderStyle.SINGLE, size: 1, color: 'E2E8F0' }
          },
          children: [
            new Paragraph({
              alignment: isNum ? AlignmentType.RIGHT : AlignmentType.LEFT,
              spacing: { before: 0, after: 0, line: 200 },
              children: [
                new TextRun({ text: val || '-', size: 13, font: 'Calibri', color: '000000' })
              ]
            })
          ]
        });
      })
    }));

    const mainTable = new Table({
      layout: TableLayoutType.FIXED,
      width: { size: 100, type: WidthType.PERCENTAGE },
      rows: [headerRow, ...dataRows]
    });

    const doc = new Document({
      sections: [
        {
          properties: {
            page: {
              size: {
                orientation: PageOrientation.LANDSCAPE
              },
              margin: {
                top: 500,
                bottom: 500,
                left: 500,
                right: 500
              }
            }
          },
          headers: {
            default: new Header({
              children: [
                new Paragraph({
                  children: [
                    new TextRun({ text: title, bold: true, size: 18, font: 'Calibri', color: '1E293B' }),
                    new TextRun({ text: `   |   Generated: ${new Date().toLocaleString()}`, size: 13, font: 'Calibri', color: '64748B' })
                  ]
                })
              ]
            })
          },
          footers: {
            default: new Footer({
              children: [
                new Paragraph({
                  alignment: AlignmentType.RIGHT,
                  children: [
                    new TextRun({ text: 'Supply Sense Report  |  Page ', size: 13, font: 'Calibri', color: '94A3B8' }),
                    new TextRun({ children: [PageNumber.CURRENT], size: 13, font: 'Calibri', color: '94A3B8' })
                  ]
                })
              ]
            })
          },
          children: [
            new Paragraph({
              spacing: { after: 80 },
              children: [
                new TextRun({ text: title, bold: true, size: 22, font: 'Calibri', color: '1E293B' })
              ]
            }),
            new Paragraph({
              spacing: { after: 120 },
              children: [
                new TextRun({ text: `Exported on ${new Date().toLocaleString()}`, size: 14, font: 'Calibri', color: '64748B' })
              ]
            }),
            mainTable
          ]
        }
      ]
    });

    const blob = await Packer.toBlob(doc);
    saveAs(blob, fileName.endsWith('.docx') ? fileName : `${fileName}.docx`);
  }
}

